<template>
  <div class="relative w-full max-w-fit min-w-0">
    <div ref="anchorRef" class="w-full min-w-0" @click="openMenu">
      <slot :selected-item @click="openMenu">
        <BaseButton
          type="button"
          :plain="!stateError"
          :outline="stateError"
          :color="stateError ? 'danger' : undefined"
          :small
          :disabled
          class="w-full max-w-fit min-w-0 justify-start! truncate!"
        >
          <slot v-if="selectedItem" :name="selectedItem.label">
            {{ selectedItem.label }}
          </slot>
          <div
            v-else
            class="w-full truncate"
            :class="{ 'text-primary': !stateError && !disabled }"
          >
            Maak een selectie
          </div>
        </BaseButton>
      </slot>
    </div>
    <Teleport to="body" :disabled="!teleport">
      <EscapeOverlay
        :active="menuVisible"
        :shaded="shadedOverlay"
        class="z-50"
        @escape="onCancel"
        @click.stop
      />
      <BaseCard
        v-if="menuVisible"
        ref="menuRef"
        class="absolute z-50 md:w-96!"
        :style="[
          floatingStyles,
          { width: `calc(100vw - ${FLOAT_PADDING * 2}px)` },
        ]"
      >
        <InputItemsFilter
          ref="filterListRef"
          v-model="pendingSelection"
          :items
          :search-string-callback
          :use-cards
          :item-height
          :all-open
        >
          <!-- Pass any slot content to InputItemsFilter slots of the same name -->
          <template v-for="(_, name) in $slots" #[name]="slotProps">
            <slot :name="name" v-bind="slotProps" />
          </template>
        </InputItemsFilter>
        <BaseDivider />
        <div class="flex items-center justify-between gap-2 p-4">
          <BaseButton color="primary" small @click="onConfirm">
            Bevestigen
          </BaseButton>
          <BaseButton plain small @click="onCancel"> Annuleren </BaseButton>
        </div>
      </BaseCard>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue"
import { useFloating, offset, shift, flip } from "@floating-ui/vue"
import {
  BaseCard,
  BaseButton,
  BaseDivider,
  EscapeOverlay,
  InputItemsFilter,
} from "@repowerednl/ui-component-library"

const props = defineProps({
  /**
   * Must have the following structure:
   * [
   *   { label: "label", value: "value" },
   *   ...
   * ]
   *
   * An item can optionally have a 'parent' key that references another item's
   * label key. This will group the item under the referenced item.
   *
   * Items can be hidden by adding a 'hidden' boolean property.
   */
  items: {
    type: Array,
    required: true,
    validator: (value) => {
      return value.every((item) => {
        return Object.hasOwn(item, "label") && Object.hasOwn(item, "value")
      })
    },
  },
  /**
   * Callback to customize the search string for each item. Should be a function
   * that takes an item, an entry from the 'items' prop, and returns a string.
   * The resulting string will be used to filter the items on the search query.
   *
   * Defaults to a concatenation of the item's 'label' and 'parent' properties.
   */
  searchStringCallback: {
    type: Function,
    default: null,
  },
  teleport: Boolean,
  /**
   * Render each item as a card, allowing for more complex slot content.
   */
  useCards: Boolean,
  /**
   * Start with all groups open.
   */
  allOpen: Boolean,
  /**
   * Height of each item in pixels.
   */
  itemHeight: {
    type: Number,
    default: 40,
  },
  /**
   * Spacing between items in pixels.
   */
  itemSpacing: {
    type: Number,
    default: 8,
  },
  /**
   * Show a shaded overlay when the menu is open, drawing attention to the menu.
   */
  shadedOverlay: {
    type: Boolean,
    default: false,
  },
  /**
   * Make the button small.
   */
  small: Boolean,
  /**
   * Mark the button red to indicate an error.
   */
  stateError: Boolean,
  /**
   * Disable the trigger button and close the menu if it's open.
   */
  disabled: Boolean,
})

// eslint-disable-next-line vue/require-prop-types
const confirmedSelection = defineModel({ default: null })

const emit = defineEmits(["confirmed", "cancelled"])

const FLOAT_PADDING = 12 // px

const anchorRef = ref()
const menuRef = ref()
const filterListRef = ref()
const pendingSelection = ref([])
const menuVisible = ref(false)

const { floatingStyles } = useFloating(anchorRef, menuRef, {
  placement: "bottom",
  middleware: [
    offset(FLOAT_PADDING),
    shift({ padding: FLOAT_PADDING }),
    flip(),
  ],
})

/**
 * Item object corresponding to the selected value
 */
const selectedItem = computed(() => {
  return props.items.find((item) => item.value === confirmedSelection.value)
})

function openMenu() {
  pendingSelection.value = [confirmedSelection.value]
  menuVisible.value = true
}

function onConfirm() {
  menuVisible.value = false
  // Only apply if selection has changed
  if (pendingSelection.value[0] !== confirmedSelection.value) {
    confirmedSelection.value = pendingSelection.value[0]
    emit("confirmed", pendingSelection.value[0])
    return
  }
  emit("cancelled")
}

function onCancel() {
  menuVisible.value = false
  emit("cancelled")
}

/**
 * Update pending selection when confirmed selection changes
 */
watch(
  confirmedSelection,
  (newSelection) => (pendingSelection.value = [newSelection]),
  { immediate: true },
)

/**
 * Focus search input when menu is opened
 */
watch(filterListRef, (newRef) => {
  if (newRef) {
    filterListRef.value.focusSearch()
  }
})

/**
 * Close the menu when the component is disabled.
 */
watch(
  () => props.disabled,
  (isDisabled) => {
    if (isDisabled && menuVisible.value) {
      onCancel()
    }
  },
  { immediate: true },
)

defineExpose({
  openMenu,
})
</script>
